import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { mdiDotsVertical } from '@mdi/js';
import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router';

import {
    ICourseFragment,
    ICourseListItemFragment,
    Maybe,
    CurrentUserDocument,
    IRole,
    IFeature,
    CopyCourseDocument,
} from 'graphql/types';
import { getUrl } from 'route/utils/getUrl';
import { CourseListItem } from 'training/components/CourseListItem';
import { List } from 'common/components/List';
import { Link } from 'common/components/Link';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { BoxLoader } from 'common/components/Loader';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { CourseExportReportDialog } from 'training/components/CourseExportReportDialog';
import { CourseExportContentDialog } from 'training/components/CourseExportContentDialog';
import { CourseAITranslateModal } from 'training/components/CourseAITranslateModal';
import { useOpenCourse } from 'training/hooks/useOpenCourse';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useFeature } from 'common/hooks/useFeature';

interface IProps {
    course?: Maybe<ICourseListItemFragment | ICourseFragment>;
    isEditable?: boolean;
    loading?: boolean;
    onReplaceCourse?(): void;
}

export const CourseFormItem = ({
    course,
    isEditable,
    loading,
    onReplaceCourse,
}: IProps) => {
    const [translate] = useTranslation();
    const navigate = useNavigate();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const [exportReportDialogOpen, setExportReportDialogOpen] = useState(false);
    const [exportContentDialogOpen, setExportContentDialogOpen] =
        useState(false);
    const [translateModalOpen, setTranslateModalOpen] = useState(false);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [copyCourse, { loading: loadingCopy }] =
        useMutation(CopyCourseDocument);
    const { data: currentUserData, loading: currentUserLoading } =
        useQuery(CurrentUserDocument);
    const { currentUser } = currentUserData || {};
    const isGroupManager = currentUser?.isGroupManager;
    const isManager = currentUser?.roles?.includes(IRole.Manager);

    const { canUse: canUseAI } = useFeature(IFeature.AI);
    const { canUse: canExport } = useFeature(IFeature.Export);

    // Only managers and groupsmanagers can see the report button
    const showReportButton = isManager || isGroupManager;

    const {
        openCourseView,
        openCourseEdit,
        loadingCourseView,
        loadingCourseEdit,
    } = useOpenCourse();

    const isLoadingItem = loading || currentUserLoading;

    if (isLoadingItem) return <BoxLoader height="197px" />;

    if (!course) {
        return (
            <Button
                disabled={loadingCourseView}
                loading={loadingCourseEdit}
                sx={{ width: '100%', height: '150px' }}
                variant="contained"
                onClick={onReplaceCourse}
            >
                {translate('selectCourse')}
            </Button>
        );
    }

    const canCopyCourse = !onReplaceCourse;

    const handleCopyCourse = async () => {
        try {
            const response = await copyCourse({
                variables: { id: course.id },
                update: (cache) => {
                    // Remove course list from cache so it will be refetched
                    cache.evict({ fieldName: 'courses' });
                    cache.gc();
                },
            });

            const newCourse = response.data?.copyCourse?.course;
            if (!newCourse) return;

            displaySnackbar(translate('courseActionSuccess.copy'), {
                variant: 'success',
            });

            setCopyDialogOpen(false);
            navigate(getUrl('COURSE_EDIT', { id: newCourse.id }));
        } catch (error) {
            showApolloError(error);
            setCopyDialogOpen(false);
        }
    };

    const showActionDropdown =
        isManager || showReportButton || !!onReplaceCourse || canCopyCourse;

    return (
        <List sx={{ backgroundColor: 'grey.50' }}>
            <CourseListItem
                disabled
                button={false}
                course={course}
                footer={
                    <Grid container>
                        <Grid size="grow">
                            <Stack direction="row" spacing={2}>
                                {isEditable && (
                                    <Button
                                        disabled={loadingCourseView}
                                        loading={loadingCourseEdit}
                                        variant="contained"
                                        onClick={() =>
                                            openCourseEdit(course.id)
                                        }
                                    >
                                        {translate('edit')}
                                    </Button>
                                )}

                                {/* Only show "view" button if course is publised */}
                                {!!course.publishedVersionNumber && (
                                    <Button
                                        disabled={loadingCourseEdit}
                                        loading={loadingCourseView}
                                        variant="contained"
                                        onClick={() =>
                                            openCourseView(course.id)
                                        }
                                    >
                                        {translate('view')}
                                    </Button>
                                )}
                            </Stack>
                        </Grid>
                        {showActionDropdown && (
                            <Grid
                                component={Box}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <DropdownMenu
                                    anchor={
                                        <Button
                                            startIcon={
                                                <Icon
                                                    path={mdiDotsVertical}
                                                    size="1.9rem"
                                                />
                                            }
                                            variant="contained"
                                        >
                                            {translate('actions')}
                                        </Button>
                                    }
                                    placement="bottom-end"
                                >
                                    <Stack
                                        divider={<Divider sx={{ my: 1.25 }} />}
                                    >
                                        <>
                                            {canCopyCourse && (
                                                <MenuItem
                                                    disabled={loadingCopy}
                                                    tabIndex={0}
                                                    onClick={() =>
                                                        setCopyDialogOpen(true)
                                                    }
                                                >
                                                    {translate('copy')}
                                                </MenuItem>
                                            )}

                                            {showReportButton && (
                                                <>
                                                    <MenuItem
                                                        component={Link}
                                                        query={`?q=${course.title}`}
                                                        tabIndex={0}
                                                        to="COURSE_REPORT"
                                                    >
                                                        {translate('reporting')}
                                                    </MenuItem>

                                                    {canExport && (
                                                        <MenuItem
                                                            tabIndex={0}
                                                            onClick={() =>
                                                                setExportReportDialogOpen(
                                                                    true
                                                                )
                                                            }
                                                        >
                                                            {translate(
                                                                'exportResults'
                                                            )}
                                                        </MenuItem>
                                                    )}
                                                </>
                                            )}
                                            {isManager && (
                                                <MenuItem
                                                    tabIndex={0}
                                                    onClick={() =>
                                                        setExportContentDialogOpen(
                                                            true
                                                        )
                                                    }
                                                >
                                                    {translate('exportContent')}
                                                </MenuItem>
                                            )}

                                            {isManager && canUseAI && (
                                                <MenuItem
                                                    tabIndex={0}
                                                    onClick={() =>
                                                        setTranslateModalOpen(
                                                            true
                                                        )
                                                    }
                                                >
                                                    {translate('translate')}
                                                </MenuItem>
                                            )}
                                        </>

                                        {!!onReplaceCourse && (
                                            <MenuItem
                                                tabIndex={0}
                                                onClick={onReplaceCourse}
                                            >
                                                {translate('replaceCourse')}
                                            </MenuItem>
                                        )}
                                    </Stack>
                                </DropdownMenu>
                            </Grid>
                        )}
                    </Grid>
                }
                px={2}
                py={0}
                sx={{
                    flexWrap: 'wrap',
                }}
            />
            <CourseExportReportDialog
                courseId={course.id}
                open={exportReportDialogOpen}
                onClose={() => setExportReportDialogOpen(false)}
            />
            <CourseExportContentDialog
                courseId={course.id}
                currentCourseVersion={
                    course.publishedVersionNumber || undefined
                }
                open={exportContentDialogOpen}
                onClose={() => setExportContentDialogOpen(false)}
            />
            <CourseAITranslateModal
                courseId={course.id}
                open={translateModalOpen}
                onClose={() => setTranslateModalOpen(false)}
            />
            <ConfirmDialog
                confirmText={translate('copy')}
                loading={loadingCopy}
                open={copyDialogOpen}
                title={translate('copyCourseMessage.title')}
                onCancel={() => setCopyDialogOpen(false)}
                onClose={() => setCopyDialogOpen(false)}
                onConfirm={handleCopyCourse}
            >
                {translate('copyCourseMessage.text')}
            </ConfirmDialog>
        </List>
    );
};
